/**
 * @fileoverview สถานะประกันของเครื่อง (ประกันลูกค้า / ประกันโรงงาน) - กติกาเดียวกับ backend utils/warranty.js
 * @module utils/warranty
 *
 * ประกันต่ออายุได้หลายรอบ (asset.customer_warranty[] / asset.company_warranty[] เรียงตามลำดับที่เพิ่ม)
 * "อยู่ในประกัน ณ วันที่ D" = มีรอบใดรอบหนึ่งที่ วันเริ่ม <= D <= วันหมด (นับรวมทั้งสองวัน เทียบเป็นรายวัน)
 * เดิมเช็คแค่วันหมดที่ไกลสุด ทำให้ช่วงว่างระหว่าง "หมดรอบเดิม" กับ "วันเริ่มรอบที่ต่อใหม่" ถูกนับเป็นในประกันผิด (2026-09-16)
 *
 * วันเริ่มของแต่ละรอบ (กติกาเดียวกับที่ฟอร์มใช้คำนวณวันหมดประกัน getWarrantyBaseDate):
 * - รอบที่ 1: {section}_warranty_start_date (มีเฉพาะประกันโรงงาน) ถ้าว่างใช้ coi_date (EOI) ของเครื่อง
 *   ถ้าไม่มีทั้งคู่ (ข้อมูลเก่า) = ไม่จำกัดวันเริ่ม เช็คแค่วันหมดเหมือนเดิม
 * - รอบที่ 2 ขึ้นไป: {section}_warranty_renew_date ถ้าว่าง (ข้อมูลเก่าก่อนมี field) ใช้วันหมดของรอบก่อนหน้า + 1 วัน = ต่อเนื่องไม่มีช่องว่าง
 * - ไม่มีแถวเลย ค่อยใช้คอลัมน์ {section}_warranty_expire_date บนเครื่องเป็นรอบเดียว เริ่มที่ coi_date
 */

/** วันที่แบบ yyyy-mm-dd ตามเวลาเครื่องผู้ใช้ (เหมือน format_date) - รับได้ทั้ง ISO string, yyyy-mm-dd และ Date */
const toDayKey = (value) => {
  if (!value) return "";
  const raw = typeof value === "string" ? value.trim() : value;
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

/**
 * ช่วงคุ้มครองของแต่ละรอบ [{ start, expire }] ตามลำดับแถว
 * @param {object} asset - เครื่อง (จาก API หรือ state ของฟอร์ม) ต้องมี coi_date และ {section}_warranty[]
 * @param {"customer"|"company"} section
 */
export const getWarrantyPeriods = (asset, section = "customer") => {
  const expireKey = `${section}_warranty_expire_date`;
  const renewKey = `${section}_warranty_renew_date`;
  const startKey = `${section}_warranty_start_date`;
  const coiDate = asset?.coi_date || "";

  const rows = (Array.isArray(asset?.[`${section}_warranty`]) ? asset[`${section}_warranty`] : []).filter(
    (row) => row && row.is_active !== false && String(row[expireKey] || "").trim() !== ""
  );

  if (!rows.length) {
    const fallback = asset?.[expireKey] || "";
    return fallback ? [{ start: coiDate, expire: fallback }] : [];
  }

  return rows.map((row, index) => ({
    start: index === 0 ? row[startKey] || coiDate : row[renewKey] || addDaysToDate(rows[index - 1][expireKey], 1),
    expire: row[expireKey],
  }));
};

/**
 * สถานะประกัน ณ วันที่อ้างอิง (ค่าเริ่มต้น = วันนี้)
 * @returns {{ expired: boolean, expireDate: string, startDate: string }}
 * - expired = false เมื่อมีรอบที่ครอบวันอ้างอิง
 * - expireDate = วันหมดของรอบที่ครอบ ถ้าไม่มีรอบไหนครอบ = วันหมดของรอบล่าสุดที่เริ่มไปแล้ว
 *   (แสดง "หมดประกัน (วันหมดรอบที่ผ่านมา)" ไม่ใช่วันหมดของรอบที่ยังไม่ถึงวันเริ่ม) ไม่มีเลย = ""
 */
export const resolveWarrantyStatus = (asset, section = "customer", referenceDate = new Date()) => {
  const periods = getWarrantyPeriods(asset, section);
  const referenceKey = toDayKey(referenceDate);

  const covers = (period) => {
    const expireKey = toDayKey(period.expire);
    if (!referenceKey || !expireKey || referenceKey > expireKey) return false;
    const startKey = toDayKey(period.start);
    return !startKey || startKey <= referenceKey;
  };

  const covering = periods.find(covers);
  if (covering) return { expired: false, expireDate: covering.expire, startDate: covering.start || "" };

  const started = periods.filter((period) => {
    const startKey = toDayKey(period.start);
    return !startKey || (referenceKey && startKey <= referenceKey);
  });
  const last = started[started.length - 1];
  return { expired: true, expireDate: last?.expire || "", startDate: last?.start || "" };
};

/** ทางลัด: เครื่องนี้หมดประกัน (section) ณ วันที่อ้างอิงหรือไม่ */
export const isAssetWarrantyExpired = (asset, section = "customer", referenceDate = new Date()) =>
  resolveWarrantyStatus(asset, section, referenceDate).expired;

/* ---------- คำนวณวันหมดประกัน (นับรวมวันเริ่มเป็นวันแรก) ---------- */
// ทำเป็นวันที่ล้วน (Date.UTC) ไม่ผ่าน toISOString กัน timezone เลื่อนวัน

const parseYmd = (value) => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (match) return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return { y: date.getFullYear(), m: date.getMonth(), d: date.getDate() };
};

const formatYmd = (utcMs) => new Date(utcMs).toISOString().split("T")[0];

/** วันที่ + N วัน (N ติดลบได้) -> yyyy-mm-dd */
export const addDaysToDate = (dateString, days) => {
  const p = parseYmd(dateString);
  if (!p) return "";
  return formatYmd(Date.UTC(p.y, p.m, p.d + Number(days || 0)));
};

/**
 * วันหมดประกัน = วันเริ่ม + N เดือน - 1 วัน (วันเริ่มนับเป็นวันแรก)
 * ถ้าเดือนปลายทางไม่มีวันที่นั้น (ต้อง clamp เช่น 31 ม.ค. + 1 เดือน) = วันสุดท้ายของเดือนปลายทาง (ไม่ -1)
 * ตัวอย่าง: 2026-10-01 + 12 -> 2027-09-30 · 2027-01-31 + 1 -> 2027-02-28 · 2028-01-30 + 1 -> 2028-02-29 · 03-01 + 1 -> 03-31
 * N = 0 คืนวันเริ่มเอง (ไม่ถอยก่อนวันเริ่ม)
 */
export const calcWarrantyExpireDate = (startDateString, months) => {
  const p = parseYmd(startDateString);
  const n = parseInt(months, 10);
  if (!p || Number.isNaN(n)) return "";
  if (n === 0) return formatYmd(Date.UTC(p.y, p.m, p.d));
  const target = p.m + n;
  const lastDay = new Date(Date.UTC(p.y, target + 1, 0)).getUTCDate();
  if (p.d > lastDay) return formatYmd(Date.UTC(p.y, target, lastDay));
  return formatYmd(Date.UTC(p.y, target, p.d) - 24 * 60 * 60 * 1000);
};

/** จำนวนเดือนที่ย้อนกลับจาก calcWarrantyExpireDate ได้ = N มากสุดที่วันหมดของ N เดือน <= วันหมดที่ระบุ */
export const calcWarrantyMonthDiff = (startDateString, expireDateString) => {
  const s = parseYmd(startDateString);
  const e = parseYmd(expireDateString);
  if (!s || !e) return 0;
  const estimate = (e.y - s.y) * 12 + (e.m - s.m);
  const expireKey = formatYmd(Date.UTC(e.y, e.m, e.d));
  for (let n = estimate + 1; n > 0; n -= 1) {
    const candidate = calcWarrantyExpireDate(startDateString, n);
    if (candidate && candidate <= expireKey) return n;
  }
  return 0;
};
